import React, { useCallback, useEffect, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useDispatch, useSelector } from 'react-redux'
import { startCase } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { getGames } from 'redux-thunk/thunk/casino.thunk'
import {
  addFavouriteGame,
  getAllFavouriteGames,
  getCasinoProvidersList,
  removeFavouriteGame
} from 'redux-thunk/thunk/game.thunk'
import { GAME_CATEGORY, LOADER_HANDLER_TYPES, SORT_BY_OPTIONS } from 'constants/index'
import Loader from 'components/ui-kit/Loader/index'
import { setShowSearchPopup } from 'redux-thunk/redux/slices/settings.slice'
import ProvidersSearchBar from '../ProvidersSearchBar/index'
import ContentNotFound from 'components/ui-kit/ContentNotFound/index'
import CommonPopup from 'components/ui-kit/CommonPopup/index'
import AllGamesCard from 'components/ui-kit/AllGamesCard/index'
import { useLoginBeforeRedirect } from 'hooks/useLoginBeforeRedirect'
import { getCategoryImage } from 'assets/images/casino-categories/index'
import allGamesImg from 'assets/images/casino-categories/all-games.svg'
import favouritesImg from 'assets/images/casino-categories/favourites.svg'

const SearchPopup = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { user } = useSelector((state) => state.user)
  const { casinoCategoriesList, favouriteGames } = useSelector((state) => state.games)
  const { viewAllGames } = useSelector((state) => state.searchPopup)
  const { loginData } = useSelector((state) => state.auth)
  const {
    [LOADER_HANDLER_TYPES.categoryWiseGamesList]: loading,
    [LOADER_HANDLER_TYPES.submit]: actionLoading
  } = useSelector((state) => state.loader)

  const [items, setItems] = useState()
  const [type, setType] = useState('')
  const [provider, setProvider] = useState('')
  const [name, setName] = useState('')
  const [gameId, setGameId] = useState(null)
  const [sortBy, setSortBy] = useState(Object.keys(SORT_BY_OPTIONS)[0])
  const { handleLoginBeforeRedirect } = useLoginBeforeRedirect()

  const limit = 35

  const modalCloseHandler = () => dispatch(setShowSearchPopup(false))

  const favouriteGameHandler = (e, game) => {
    setGameId(game?.id)
    e?.stopPropagation()
    if (game?.isFavourite || type === GAME_CATEGORY.FAVOURITE) {
      dispatch(removeFavouriteGame({ game }))
    } else {
      dispatch(addFavouriteGame({ game }))
    }
  }

  const onGamePlayRedirect = useCallback(
    (game) => {
      const gameName = game.gameName.toLowerCase().replace(/\s+/g, '-')
      if (loginData?.accessToken) {
        history?.push(`/casino/play-game/${gameName}`, { game, demo: false })
        dispatch(setShowSearchPopup(false))
      } else if (game.hasDemo) {
        history?.push(`/casino/play-game/${gameName}`, { game, demo: true })
        dispatch(setShowSearchPopup(false))
      } else {
        handleLoginBeforeRedirect(`/casino/play-game/${gameName}`)
      }
    },
    [loginData]
  )

  const fetchMore = () => {
    if (type === GAME_CATEGORY.FAVOURITE) {
      dispatch(getAllFavouriteGames({ data: { limit, offset: items?.rows?.length, provider, name, sortBy: SORT_BY_OPTIONS[sortBy] } }))
    } else {
      dispatch(getGames({ data: { limit, offset: items?.rows?.length, type, provider, userId: user?.id, name, sortBy: SORT_BY_OPTIONS[sortBy] } }))
    }
  }

  useEffect(() => {
    if (type === GAME_CATEGORY.FAVOURITE) {
      setItems({ ...favouriteGames, rows: favouriteGames?.rows?.map(el => ({ ...el, CasinoGame: { ...el?.CasinoGame, isFavourite: true } })) })
    } else {
      setItems(viewAllGames)
    }
  }, [viewAllGames, favouriteGames])

  useEffect(() => {
    if (type === GAME_CATEGORY.FAVOURITE) {
      dispatch(getAllFavouriteGames({ data: { limit, offset: 0, provider, name, sortBy: SORT_BY_OPTIONS[sortBy] } }))
    } else {
      dispatch(getGames({ data: { limit, offset: 0, type, provider, userId: user?.id, name, sortBy: SORT_BY_OPTIONS[sortBy] } }))
    }
  }, [type, name, provider, sortBy])

  useEffect(() => {
    dispatch(getCasinoProvidersList())
    document.body?.classList.add('overflowHidden')
    return () => document.body?.classList.remove('overflowHidden')
  }, [])

  const activeLabel = type === ''
    ? t('allGames')
    : type === GAME_CATEGORY.FAVOURITE
    ? t('favourites')
    : startCase(type)

  // Resolve the image for a category item — prefer backend `image` key, fall back to name
  const resolveCategoryImg = (item) => {
    const key = item?.image || item?.gameCategory
    return getCategoryImage(key)
  }

  return (
    <CommonPopup
      modalCloseHandler={modalCloseHandler}
      id='search_modal'
      modalBodyClasses='p-0'
      modalDialogClass='search-dilog'
      modalContentClass='search-modal'
    >
      <div className='sp-wrap'>
        {/* ── Left sidebar ────────────────────────────────────── */}
        <aside className='sp-sidebar d-none d-lg-flex'>
          <div className='sp-sidebar-inner'>
            <p className='sp-sidebar-label'>Browse</p>

            <button
              className={`sp-nav-btn ${type === '' ? 'active' : ''}`}
              onClick={() => setType('')}
            >
              <span className='sp-nav-icon'>
                <img src={allGamesImg} alt='All Games' className='sp-cat-img' />
              </span>
              <span className='sp-nav-text'>{t('allGames')}</span>
              {type === '' && <ChevronRight size={14} className='sp-nav-arrow' />}
            </button>

            {loginData?.accessToken && (
              <button
                className={`sp-nav-btn ${type === GAME_CATEGORY.FAVOURITE ? 'active' : ''}`}
                onClick={() => setType(GAME_CATEGORY.FAVOURITE)}
              >
                <span className='sp-nav-icon'>
                  <img src={favouritesImg} alt='Favourites' className='sp-cat-img' />
                </span>
                <span className='sp-nav-text'>{t('favourites')}</span>
                {type === GAME_CATEGORY.FAVOURITE && <ChevronRight size={14} className='sp-nav-arrow' />}
              </button>
            )}

            {casinoCategoriesList?.length > 0 && (
              <p className='sp-sidebar-label sp-sidebar-label--mt'>Categories</p>
            )}

            {casinoCategoriesList?.map((item) => (
              <button
                key={uuidv4()}
                className={`sp-nav-btn ${type === item?.gameCategory ? 'active' : ''}`}
                onClick={() => setType(item?.gameCategory)}
              >
                <span className='sp-nav-icon'>
                  <img src={resolveCategoryImg(item)} alt={item?.gameCategory} className='sp-cat-img' />
                </span>
                <span className='sp-nav-text'>{startCase(item?.gameCategory)}</span>
                {type === item?.gameCategory && <ChevronRight size={14} className='sp-nav-arrow' />}
              </button>
            ))}
          </div>
        </aside>

        {/* ── Main content ─────────────────────────────────────── */}
        <div className='sp-main'>
          {/* Heading */}
          <div className='sp-heading'>
            <div className='sp-heading-left'>
              <h4 className='sp-title'>{t('searchGames')}</h4>
              {activeLabel && <span className='sp-breadcrumb'>/ {activeLabel}</span>}
            </div>
            {items?.count != null && (
              <span className='sp-count'>{items.count.toLocaleString()} games</span>
            )}
          </div>

          {/* Mobile category pills */}
          <div className='sp-pills sp-pills--mobile'>
            <button
              className={`sp-pill ${type === '' ? 'active' : ''}`}
              onClick={() => setType('')}
            >
              <img src={allGamesImg} alt='All' className='sp-pill-img' />
              All
            </button>
            {loginData?.accessToken && (
              <button
                className={`sp-pill ${type === GAME_CATEGORY.FAVOURITE ? 'active' : ''}`}
                onClick={() => setType(GAME_CATEGORY.FAVOURITE)}
              >
                <img src={favouritesImg} alt='Fav' className='sp-pill-img' />
                Fav
              </button>
            )}
            {casinoCategoriesList?.map((item) => (
              <button
                key={item?.gameCategory}
                className={`sp-pill ${type === item?.gameCategory ? 'active' : ''}`}
                onClick={() => setType(item?.gameCategory)}
              >
                <img src={resolveCategoryImg(item)} alt={item?.gameCategory} className='sp-pill-img' />
                {startCase(item?.gameCategory)}
              </button>
            ))}
          </div>

          {/* Search bar */}
          <ProvidersSearchBar
            setName={setName}
            setProvider={setProvider}
            provider={provider}
            showProvidersDropdown
            sortBy={sortBy}
            setSortBy={setSortBy}
          />

          {/* Game grid */}
          <section className='sp-games global-casino-section'>
            {items?.rows?.length === 0 && !loading
              ? <ContentNotFound message={t('noMatchesFound')} />
              : (
                <div className='sp-game-grid'>
                  {items?.rows?.map((game) => {
                    const item = type === GAME_CATEGORY.FAVOURITE ? game?.CasinoGame : game
                    return (
                      <AllGamesCard
                        key={item?.casinoGameId}
                        game={item}
                        item={item}
                        onGamePlayRedirect={onGamePlayRedirect}
                        isLoggedIn={loginData?.accessToken}
                        favouriteGameHandler={favouriteGameHandler}
                        gameId={gameId}
                        actionLoading={actionLoading}
                        isFavourite={item?.isFavourite}
                        searchPopup
                        cardClasses=''
                      />
                    )
                  })}
                </div>
              )}
            {loading && <Loader variant={LOADER_HANDLER_TYPES.content} />}
            {items?.rows?.length < items?.count && (
              <div className='sp-load-more'>
                <button
                  type='button'
                  className='sp-load-more-btn'
                  onClick={fetchMore}
                  disabled={loading}
                >
                  Load more games
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </CommonPopup>
  )
}

export default React.memo(SearchPopup)
